/**
 * Wire types for the RFP API. They mirror the DTOs returned by the backend
 * (`backend/src/modules/**`) one-to-one - keep both sides in step.
 */

export const RFP_STATUSES = [
  'draft',
  'send_to_vendors',
  'in_progress',
  'respond_received',
  'closed',
] as const;

export type RfpStatus = (typeof RFP_STATUSES)[number];

export const VENDOR_TYPES = ['new', 'existing', 'past_contact'] as const;
export type VendorType = (typeof VENDOR_TYPES)[number];

export const RFP_VENDOR_STATUSES = [
  'pending',
  'contacted',
  'responded',
  'submitted',
  'completed',
  'declined',
] as const;
export type RfpVendorStatus = (typeof RFP_VENDOR_STATUSES)[number];

export interface Pagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  /** "1-5 of 5", exactly as the design renders it. */
  rangeLabel: string;
}

export interface Paginated<T> {
  items: T[];
  pagination: Pagination;
}

export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export interface ApiErrorEnvelope {
  success: false;
  error: {
    message: string;
    details?: { field: string; message: string }[];
  };
}

export interface ProductCategory {
  id: number;
  name: string;
  description: string | null;
  isActive: boolean;
  productCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Product {
  id: number;
  productCategoryId: number;
  categoryName: string | null;
  name: string;
  sku: string | null;
  unit: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Vendor {
  id: number;
  vendorCode: string;
  name: string;
  vendorType: VendorType;
  contactPerson: string | null;
  contactNumber: string | null;
  email: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  gstNumber: string | null;
  isActive: boolean;
  rfpCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RfpListItem {
  id: number;
  rfpNumber: string;
  title: string | null;
  status: RfpStatus;
  createdOn: string;
  dueDate: string | null;
  daysLeft: number | null;
  productCategoryId: number | null;
  productCategoryName: string | null;
  /** Every category the RFP's products belong to - the RFP's own category first. */
  categories: { count: number; names: string[] };
  products: { count: number; names: string[] };
  vendorCount: number;
  contactedCount: number;
  responseCount: number;
  submittedCount: number;
  completedCount: number;
}

export interface RfpProductLine {
  id: number;
  productId: number;
  name: string;
  sku: string | null;
  unit: string;
  quantity: number;
  targetPrice: number | null;
}

export interface RfpVendorLink {
  id: number;
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  vendorType: VendorType;
  contactPerson: string | null;
  contactNumber: string | null;
  email: string | null;
  status: RfpVendorStatus;
  quotedAmount: number | null;
  contactedAt: string | null;
  respondedAt: string | null;
  submittedAt: string | null;
  completedAt: string | null;
  remark: string | null;
}

export interface RfpDocument {
  id: number;
  rfId?: number;
  rfpId: number | null;
  vendorId: number | null;
  documentType: 'quotation' | 'vendor_response' | 'attachment';
  originalName: string;
  storedName: string;
  mimeType: string;
  fileSize: number;
  fileSizeLabel: string;
  checksum: string | null;
  uploadedAt: string;
}

export interface RfpStatusHistoryEntry {
  id: number;
  fromStatus: string | null;
  toStatus: string;
  changedBy: string | null;
  note: string | null;
  createdAt: string;
}

export interface RfpDetail extends RfpListItem {
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  productLines: RfpProductLine[];
  vendors: RfpVendorLink[];
  documents: RfpDocument[];
  history: RfpStatusHistoryEntry[];
}

export interface RfpStats {
  total: number;
  draft: number;
  sendToVendors: number;
  inProgress: number;
  respondReceived: number;
  closed: number;
  totalVendorsInvited: number;
}

export interface VendorStats {
  total: number;
  new: number;
  existing: number;
  pastContact: number;
  invited: number;
}

export interface ExtractedProduct {
  id: number;
  name: string;
  sku: string | null;
  unit: string;
}

export interface ExtractedVendor {
  id: number;
  vendorCode: string;
  name: string;
  vendorType: VendorType;
  contactPerson: string | null;
  contactNumber: string | null;
  email: string | null;
  selected: boolean;
}

export interface ExtractionUsage {
  provider: 'gemini';
  callType: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  latencyMs: number;
  ok: boolean;
}

export type ExtractionEngine = 'gemini' | 'heuristic';

/** What happened to the outbound RFP emails when a send was part of the request. */
export interface MailDispatchSummary {
  transport: string;
  dryRun: boolean;
  sandboxRedirect: string | null;
  attempted: number;
  /** Messages handed to the mail provider. */
  sent: number;
  /** Messages recorded but not sent, because the transport is a dry run. */
  previewed: number;
  failed: number;
  skipped: number;
  note?: string;
  results: {
    vendorId: number;
    vendorCode: string;
    vendorName: string;
    to: string | null;
    status: 'sent' | 'dry_run' | 'failed' | 'skipped';
    providerMessageId: string | null;
    error?: string;
  }[];
}

/* ---------------------------------------------------------------------------
 * Vendor replies, quote comparison, evaluation and follow-up (POC parts 3-4)
 * ------------------------------------------------------------------------- */

/** How a reply was attributed to an RFP and a vendor. */
export type QuoteMatchMethod =
  | 'in_reply_to'
  | 'thread_token'
  | 'sender_email'
  | 'unmatched'
  | 'loop_guard';

export interface MailMessage {
  id: number;
  direction: 'outbound' | 'inbound';
  kind: 'rfp' | 'reminder';
  status: string;
  vendorId: number | null;
  vendorCode: string | null;
  vendorName: string | null;
  threadToken: string | null;
  subject: string | null;
  fromEmail: string | null;
  toEmail: string | null;
  intendedToEmail: string | null;
  attachmentCount: number;
  matchMethod: QuoteMatchMethod | null;
  bodyPreview: string | null;
  parsedEngine: string | null;
  parsedAt: string | null;
  errorMessage: string | null;
  /** When the message was sent or received (newest messages last). */
  at: string | null;
}

export interface QuoteVendorSummary {
  vendorId: number;
  vendorCode: string | null;
  vendorName: string | null;
  currency: string | null;
  itemsTotal: number | null;
  grandTotal: number | null;
  validity: string | null;
  leadTimeDays: number | null;
  paymentTerms: string | null;
  quotedLines: number;
  requestedLines: number;
  parsedEngine: string | null;
  parsedAt: string | null;
  deviations: string[];
  remarks: string | null;
}

export interface QuoteGridRow {
  rfpProductId: number;
  name: string;
  quantity: number | null;
  uom: string | null;
  quotes: {
    vendorId: number;
    vendorName: string | null;
    unitPrice: number | null;
    lineTotal: number | null;
    brand: string | null;
    description: string | null;
    matched: boolean;
  }[];
}

export interface EvaluationRanking {
  vendorId: number | null;
  rank: number | null;
  score: number | null;
  coverageNote: string | null;
  priceNote: string | null;
  strengths: string[];
  risks: string[];
  deviations: string[];
}

/** The free-form payload an evaluation run stores (`rfp_evaluations.payload`). */
export interface EvaluationPayload {
  summary?: string;
  ranking?: EvaluationRanking[];
  missingInformation?: string[];
  nextSteps?: string[];
}

export type EvaluationStatus = 'ok' | 'insufficient_data' | 'failed';

export interface RfpEvaluation {
  id: number;
  rfpId: number;
  engine: ExtractionEngine;
  model: string | null;
  status: EvaluationStatus;
  recommendedVendorId: number | null;
  recommendedVendorName: string | null;
  currency: string | null;
  recommendation: string | null;
  payload: EvaluationPayload | null;
  vendorCount: number;
  quoteCount: number;
  totalTokens: number;
  latencyMs: number;
  errorMessage: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface ResponseVendorState {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  status: RfpVendorStatus;
  email: string | null;
  hasReply: boolean;
  reminderSent: boolean;
  overdue: boolean;
}

/** A Stage 2 -> 3 -> 4 move performed by an evaluation run. */
export interface VendorStageChange {
  vendorId: number;
  from: string;
  to: RfpVendorStatus;
}

/** One inbound vendor message and the auto-reply composed for it (`GET /rfps/:id/responses`). */
export interface AutoReplyInteraction {
  messageId: number;
  vendorId: number | null;
  vendorCode: string | null;
  vendorName: string | null;
  senderEmail: string | null;
  senderName: string | null;
  subject: string | null;
  receivedAt: string | null;
  /** The pipeline's own state for the delivery: received, processed, failed, rejected, ignored. */
  messageStatus: string;
  outcomeCode: string | null;
  securityFlags: string[];
  attachmentCount: number;
  rejectedAttachmentCount: number;
  bodyPreview: string | null;
  /** The retention sweep has blanked the stored text: "no longer kept", not "nothing was said". */
  bodyPurged: boolean;
  reply: {
    id: number;
    status: string;
    action: string;
    subject: string | null;
    body: string | null;
    bodyPurged: boolean;
    to: string | null;
    sentAt: string | null;
    decisionCode: string | null;
    decisionDetail: string | null;
    errorCode: string | null;
    errorDetail: string | null;
    model: string | null;
    createdAt: string | null;
  } | null;
}

/** `GET /api/rfps/:id/responses` (the evaluate response adds `stageChanges`). */
export interface RfpResponses {
  rfpId: number;
  thread: MailMessage[];
  /** What the auto-reply service did with each inbound message, newest first. */
  autoReplies: AutoReplyInteraction[];
  quotes: {
    vendors: QuoteVendorSummary[];
    items: QuoteGridRow[];
    currency: string | null;
    totalsComparable: boolean;
  };
  evaluation: RfpEvaluation | null;
  vendors: ResponseVendorState[];
  summary: {
    requestedLines: number;
    repliedVendors: number;
    quotedVendors: number;
    pendingVendors: number;
    overdueVendors: number;
    reminderAfterDays: number;
  };
  stageChanges?: VendorStageChange[];
}

export interface ExtractionResult {
  documentId: number;
  documentName: string;
  generatedAt: string;
  source: 'ai';
  /** Which extractor produced this payload - Gemini or the offline fallback. */
  engine: ExtractionEngine;
  /** Gemini model id, null when the heuristic extractor ran. */
  model: string | null;
  /** Why Gemini was skipped or failed, null when it produced this payload. */
  fallbackReason: string | null;
  /** Token / latency accounting of the model call, when one was made. */
  usage: ExtractionUsage | null;
  /**
   * Full document-level extraction (line items, supplier, totals...) as returned
   * by Gemini. Null on the heuristic path; the wizard does not render it yet.
   */
  document: Record<string, unknown> | null;
  /** Plain-text summary of the document, shown in the "AI Generated Information" panel. */
  summary: string | null;
  /** Where that summary came from: the model, or the local template fallback. */
  summarySource: 'gemini' | 'template' | null;
  productInformation: {
    title: string;
    productCategoryId: number | null;
    productCategoryName: string | null;
    products: ExtractedProduct[];
    quantity: number;
    notes: string;
  };
  suggestedVendors: ExtractedVendor[];
}

export interface CreateRfpPayload {
  title?: string;
  productCategoryId?: number;
  status?: RfpStatus;
  createdOn?: string;
  dueDate?: string;
  notes?: string;
  createdBy?: string;
  documentId?: number;
  productIds?: number[];
  vendorIds?: number[];
  sendToVendors?: boolean;
}

/* ---------------------------------------------------------------------------
 * Authentication
 * ------------------------------------------------------------------------- */

export const USER_ROLES = ['admin', 'manager', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: UserRole;
}

export interface LoginResult {
  token: string;
  tokenType: 'Bearer';
  /** Configured lifetime, e.g. "8h". */
  expiresIn: string;
  /** Absolute ISO expiry so the client can refresh before the token dies. */
  expiresAt: string;
  user: AuthUser;
}
